import React, { useEffect, useMemo, useState } from "react";
import { TourProvider, useTour, components as base } from "@reactour/tour";
import { uiAction } from "../../store/ui-gemini";
import { useDispatch } from "react-redux";

// Đồng bộ state 'open' của bạn vào Tour context
function OpenSync({ open, startAt = 0 }) {
  const { isOpen, setIsOpen, setCurrentStep } = useTour();
  React.useEffect(() => {
    if (open && !isOpen) {
      // Thêm delay để đảm bảo elements đã render xong
      setTimeout(() => {
        setCurrentStep(startAt);
        setIsOpen(true);
      }, 1000);
    }
    if (!open && isOpen) setIsOpen(false);
  }, [open, isOpen, setIsOpen, setCurrentStep, startAt]);
  return null;
}

function TourStepFlag() {
    const { isOpen, currentStep } = useTour();
    React.useEffect(() => {
    if (isOpen) {
        document.body.dataset.tourStep = String(currentStep);
    } else {
        delete document.body.dataset.tourStep;
    }
    }, [isOpen, currentStep]);
    return null;
}

function waitForSelector(selector, timeout = 6000) {
    return new Promise((resolve, reject) => {
      const start = performance.now();
      const check = () => {
        const el = document.querySelector(selector);
        if (el) {
          const r = el.getBoundingClientRect();
          if (r.width > 0 && r.height > 0) return resolve(el);
        }
        if (performance.now() - start > timeout) return reject(new Error('timeout'));
        requestAnimationFrame(check);
      };
      check();
    });
  }

function makeContentComponent(stepHooks, brandColor, prepareMap, onComplete, setCompletedByDone, autoSkipMissing = true) {
  const brand = brandColor || "#024959";
  return function ContentComponent(props) {
    const { steps, currentStep, setCurrentStep, setIsOpen } = props;
    const isLast = currentStep === steps.length - 1;
    const isLastTwo = currentStep >= steps.length - 2;
    const content = steps[currentStep]?.content;

    const goNext = async () => {
          const next = currentStep + 1;
          if (isLast) {
            setIsOpen(false);
            if (typeof onComplete === 'function') {
              try { setCompletedByDone && setCompletedByDone(true); } catch {}
              try { onComplete(); } catch {}
            }
            return;
          }
          const nextStep = steps[next];
          try {
            if (prepareMap && typeof prepareMap[next] === 'function') {
              await Promise.resolve(prepareMap[next]());
            }
            if (nextStep?.selector) {
              await waitForSelector(nextStep.selector, 6000);
            }
            setCurrentStep(next);
          } catch (e) {
            // If still can't find element: skip or alert user
            if (autoSkipMissing) setCurrentStep(next);
            else alert('Please open the correct area (menu/modal) and click Next again.');
          }
        };

    useEffect(() => {
      if (stepHooks && stepHooks[currentStep]) {
        Promise.resolve(stepHooks[currentStep]()).catch(() => {});
      }
      return () => {};
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentStep]);

    return (
      <div style={{
        width: 320, borderRadius: 12, background: "rgba(255,255,255,0.97)",
        border: "1px solid #e5e7eb", boxShadow: "0 20px 60px rgba(2,73,89,.15)", padding: 12,
        marginTop: isLastTwo ? 10 : 0
      }}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: 8, gap: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: brand }}>Welcome tour</span>
          <span style={{ marginLeft: "auto", fontSize: 12, color: "#6b7280" }}>
            {currentStep + 1}/{steps.length}
          </span>
        </div>

        <div style={{ fontSize: 14, color: "#374151", whiteSpace: "pre-line", textAlign: "left" }}>
          {typeof content === "function" ? content(props) : content}
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 12 }}>
          <button
            onClick={() => setIsOpen(false)}
            style={{ fontSize: 12, padding: "6px 8px", borderRadius: 6, border: "1px solid #e5e7eb" }}
          >
            Skip
          </button>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              onClick={() => setCurrentStep(s => Math.max(0, s - 1))}
              disabled={currentStep === 0}
              style={{ fontSize: 12, padding: "6px 8px", borderRadius: 6, border: "1px solid #e5e7eb",
                       opacity: currentStep === 0 ? .5 : 1 }}
            >
              Prev
            </button>
            <button
              onClick={goNext}
              style={{ fontSize: 12, padding: "6px 10px", borderRadius: 6, background: brand, color: "#fff" }}
            >
              {isLast ? "Done" : "Next"}
            </button>
          </div>
        </div>
      </div>
    );
  };
}

function Close({ onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        position: "absolute", right: 8, top: 8, padding: "2px 6px",
        fontSize: 11, borderRadius: 6, border: "1px solid #e5e7eb", background: "#fff"
      }}
      aria-label="Close tour"
    >
      esc
    </button>
  );
}

function Badge({ children }) {
  return (
    <base.Badge styles={{ badge: b => ({ ...b, backgroundColor: "transparent" }) }}>
      <span style={{ color: "#024959", fontWeight: 600, fontSize: 12 }}>{children}</span>
    </base.Badge>
  );
}

export default function ReactOnboardingTour({
  steps,
  tourId = "main",
  version = 1,
  autoStart = true,
  shouldShowTour = false,
  brandColor = "#024959",
  scopeKey,
  onOpenChange,
  stepHooks,
  prepareMap,
  onComplete,
  disableGuard = false,
  saveSeenOnClose = true,
  onSkip
}) {
  const storageKey = `tour:${tourId}:v${version}${scopeKey ? `:${scopeKey}` : ""}`;
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const completedByDoneRef = React.useRef(false);
  
  useEffect(() => {
    if (!autoStart) return;
    // Always allow opening; parent controls when to render
    setOpen(true);
  }, [autoStart]);

  // React to shouldShowTour prop changes
  useEffect(() => {
    if (shouldShowTour) {
      setOpen(true);
    }
  }, [shouldShowTour]);


  // Đừng lọc cứng theo DOM tại thời điểm render
  const filteredSteps = steps;

  const ContentComponent = useMemo(
      () => makeContentComponent(stepHooks, brandColor, prepareMap, onComplete, (v) => { completedByDoneRef.current = !!v; }, true /* autoSkipMissing */),
      [stepHooks, brandColor, prepareMap, onComplete]
    );


  return (
    <TourProvider
      steps={filteredSteps}
      beforeClose={() => {
        document.body.classList.remove('tour-active');
        delete document.body.dataset.tourStep;
        // Always close sidebar if it's open (works for overlay click, Skip, or Close)
        try {
          const sidebar = document.querySelector('[data-tour="sidebar-menu"]');
          const isExpanded = sidebar && sidebar.getAttribute('data-expanded') === '1';
          if (isExpanded) {
            dispatch(uiAction.toggleSideBar());
          }
        } catch {}
        if (saveSeenOnClose) {
          try { localStorage.setItem(storageKey, "1"); } catch {}
        }
        // If closed not via Done, treat as Skip
        if (!completedByDoneRef.current && typeof onSkip === 'function') {
          try { onSkip(); } catch {}
        }
        completedByDoneRef.current = false;
        onOpenChange && onOpenChange(false);
      }}
      afterOpen={() => {
        document.body.classList.add('tour-active');
        onOpenChange && onOpenChange(true);
      }}
      disableWhenSelectorFalsy={!disableGuard}
      ContentComponent={ContentComponent}
      components={{ Close, Badge }}
      scrollSmooth
      showDots={false}
      showBadge
      styles={{
        popover: base => ({ ...base, padding: 0, borderRadius: 12, boxShadow: "0 20px 60px rgba(0,0,0,.12)", zIndex: 99999, marginTop: 0 }),
        maskArea: base => ({ ...base, rx: 8 }),
        badge: b => ({ ...b, backgroundColor: brandColor }),
        dot: d => ({ ...d, backgroundColor: brandColor }),
        arrow: a => ({ ...a, color: "#fff" })
      }}
      className="reactour__popover"
      position="auto"
      disableInteraction={false}
    >
        <TourStepFlag />
      <OpenSync open={open && filteredSteps.length > 0} startAt={0} />
    </TourProvider>
  );
}
